import { get } from './client';

/** A page of tickets and where the next one starts (`next`: a ticket number for newest-first, a count for queue order). */
export interface TicketPage<T> {
  tickets: T[];
  next?: number | null;
}

/**
 * Every page of a ticket list, following `next` (the server caps a page at 500). Past `max` tickets it stops and says
 * so (`more: true`), so a long list is never cut off without a word.
 */
export async function getAllTickets<T, P extends TicketPage<T>>(path: string, o: { max?: number } = {}): Promise<P & { more: boolean }> {
  const max = o.max ?? 5000;
  const queue = /[?&]sort=queue\b/.test(path);
  const sep = path.includes('?') ? '&' : '?';
  let tickets: T[] = [];
  let next: number | null = null;
  let last: P | null = null;
  for (let page = 0; page < 20; page++) {
    const j: P = await get<P>(next == null ? path : `${path}${sep}${queue ? 'skip' : 'before'}=${next}`);
    last = j;
    tickets = tickets.concat(j.tickets ?? []);
    const n = j.next ?? null;
    // `before` only ever goes down and `skip` only up; anything else would loop forever.
    if (n == null || (next != null && (queue ? n <= next : n >= next))) return { ...j, tickets, next: null, more: false };
    next = n;
    if (tickets.length >= max) return { ...j, tickets, next, more: true };
  }
  return { ...(last as P), tickets, next, more: true };
}
